import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase, uploadImage, deleteImageByUrl, sanitizeBase64Images } from '../../services/supabaseClient';
import Modal from '../Modal';
import ImageUploader from '../ImageUploader';
import { useAuth } from '../../context/AuthContext';

// Editor rich text minimalista basado en contentEditable + execCommand.
// Cero dependencias externas. Si más adelante quieres algo más robusto
// (React Quill, TipTap, Lexical), basta con reemplazar este componente
// manteniendo la misma API: { value, onChange }.
const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  // Solo inyectamos el HTML inicial una vez para no romper la posición del cursor
  // mientras el usuario escribe.
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  }, [value]);

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
      editorRef.current.focus();
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleLink = () => {
    const url = window.prompt('URL del enlace:', 'https://');
    if (url) exec('createLink', url);
  };

  const toolbarBtn = {
    padding: '4px 10px',
    border: '1px solid #ccc',
    background: '#fff',
    borderRadius: 3,
    cursor: 'pointer',
    fontSize: 13,
  };

  return (
    <div className="rich-text-editor" style={{ border: '1px solid #ccc', borderRadius: 4 }}>
      <div
        className="rich-text-toolbar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: 6,
          borderBottom: '1px solid #eee',
          background: '#fafafa',
        }}
      >
        <button type="button" style={{ ...toolbarBtn, fontWeight: 'bold' }} onClick={() => exec('bold')} title="Negrita">
          B
        </button>
        <button type="button" style={{ ...toolbarBtn, fontStyle: 'italic' }} onClick={() => exec('italic')} title="Cursiva">
          I
        </button>
        <button
          type="button"
          style={{ ...toolbarBtn, textDecoration: 'underline' }}
          onClick={() => exec('underline')}
          title="Subrayado"
        >
          U
        </button>
        <span style={{ borderLeft: '1px solid #ddd', margin: '0 4px' }} />
        <button type="button" style={toolbarBtn} onClick={() => exec('formatBlock', 'H2')} title="Título">
          H2
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('formatBlock', 'H3')} title="Subtítulo">
          H3
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('formatBlock', 'P')} title="Párrafo">
          P
        </button>
        <span style={{ borderLeft: '1px solid #ddd', margin: '0 4px' }} />
        <button type="button" style={toolbarBtn} onClick={() => exec('insertUnorderedList')} title="Lista">
          • Lista
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('insertOrderedList')} title="Lista numerada">
          1. Lista
        </button>
        <span style={{ borderLeft: '1px solid #ddd', margin: '0 4px' }} />
        <button type="button" style={toolbarBtn} onClick={() => exec('justifyLeft')} title="Alinear izquierda">
          ⯇
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('justifyCenter')} title="Centrar">
          ≡
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('justifyRight')} title="Alinear derecha">
          ⯈
        </button>
        <span style={{ borderLeft: '1px solid #ddd', margin: '0 4px' }} />
        <button type="button" style={toolbarBtn} onClick={handleLink} title="Insertar enlace">
          🔗 Link
        </button>
        <button type="button" style={toolbarBtn} onClick={() => exec('removeFormat')} title="Limpiar formato">
          ✕ Formato
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        style={{
          minHeight: 200,
          padding: 12,
          outline: 'none',
          lineHeight: 1.5,
        }}
        suppressContentEditableWarning
      />
    </div>
  );
};

const EditorialSection = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    written_by: '',
    charge: '',
    image_url: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('editorial')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) console.error(error);
    else setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({
      title: '',
      body: '',
      written_by: user?.name || '',
      charge: '',
      image_url: '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setFormData({
      title: item.title || '',
      body: item.body || '',
      written_by: item.written_by || '',
      charge: item.charge || '',
      image_url: item.image_url || '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('¿Eliminar este editorial?')) return;
    const { error } = await supabase.from('editorial').delete().eq('id', item.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    if (item.image_url) await deleteImageByUrl(item.image_url);
    fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = await uploadImage(imageFile);
      } else if (imageFile === null && formData.image_url === '') {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = null;
      }

      // Nunca guardar imagenes base64 incrustadas: se suben al bucket primero.
      const bodyLimpio = await sanitizeBase64Images(formData.body);

      const payload = {
        title: formData.title,
        body: bodyLimpio,
        written_by: formData.written_by,
        charge: formData.charge,
        image_url: imageUrl,
      };

      if (editing) {
        const { error } = await supabase
          .from('editorial')
          .update(payload)
          .eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('editorial').insert([payload]);
        if (error) throw error;
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Quita HTML para mostrar un preview legible del cuerpo en la tabla
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div>
      <div className="add-button-container">
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Agregar Editorial
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Acciones</th>
                <th>Foto</th>
                <th>Título</th>
                <th>Contenido</th>
                <th>Escrito por</th>
                <th>Cargo</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-row">
                    Sin editoriales registrados
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const bodyText = stripHtml(item.body);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEditModal(item)}
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(item)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                      <td>
                        {item.image_url ? (
                          <img src={item.image_url} alt="" className="table-image" loading="lazy" decoding="async" />
                        ) : (
                          <div className="no-image">Sin foto</div>
                        )}
                      </td>
                      <td>{item.title}</td>
                      <td style={{ maxWidth: 300 }}>
                        {bodyText
                          ? bodyText.length > 100
                            ? bodyText.substring(0, 100) + '...'
                            : bodyText
                          : '-'}
                      </td>
                      <td>{item.written_by}</td>
                      <td>{item.charge || '-'}</td>
                      <td>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString('es-PE')
                          : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editing ? 'Editar Editorial' : 'Agregar Editorial'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {formError && <div className="error-message">{formError}</div>}

            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Contenido *</label>
              <RichTextEditor
                value={formData.body}
                onChange={(html) => setFormData((prev) => ({ ...prev, body: html }))}
              />
            </div>

            <div className="form-group">
              <label>Escrito por *</label>
              <input
                type="text"
                value={formData.written_by}
                onChange={(e) => setFormData({ ...formData, written_by: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Cargo</label>
              <input
                type="text"
                value={formData.charge}
                onChange={(e) => setFormData({ ...formData, charge: e.target.value })}
                placeholder="Ej: Director, Editor en Jefe..."
              />
            </div>

            <ImageUploader
              currentImageUrl={formData.image_url}
              onFileSelect={(file) => {
                setImageFile(file);
                if (file === null) setFormData({ ...formData, image_url: '' });
              }}
              label="Imagen (opcional)"
            />
          </form>
        </Modal>
      )}
    </div>
  );
};

export default EditorialSection;